// @ts-check
// Linha com até duas séries em SVG próprio (doc 16, 10.6): traço de 2 px, pontos, área suave sob a série principal,
// série de comparação (ano anterior) tracejada em cinza e cruz de leitura com tooltip por coluna. Cada ponto com
// registros abre a lista (contrato 07, seção 10), com um alvo invisível de 24 px em volta do ponto.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import {
  COMPARISON_SERIES_COLOR,
  ChartLegend,
  ChartTooltip,
  buildNiceScale,
  areAllIntegers,
  estimateTextWidth,
  getSeriesColor,
  layoutCategoryLabels,
  getSingleSeriesColor,
  truncateToWidth,
  useElementSize,
} from './chart-utils.js';
import { splitPlottedSeries } from './bar-chart.js';
import { formatMetricValue } from './widget-format.js';
import { handleDrillLinkClick, useChartPointNavigation, useDrilldown } from './drilldown.js';

/**
 * @typedef {import('../../data/dashboards.js').SeriesWidgetData} SeriesWidgetData
 * @typedef {import('./drilldown.js').ChartPoint & { label: string, valueText: string }} LinePoint
 */

let gradientCounter = 0;
// raio do alvo invisível de cada ponto: 24 px de diâmetro, o mínimo confortável para o clique e o toque
const POINT_HIT_RADIUS = 12;

/**
 * Série de comparação: chave com "previous" (convenção do contrato para o ano anterior) ou a segunda série
 * dos indicadores que comparam com o ano anterior.
 * @param {{ key: string }} series
 * @param {number} index
 * @param {boolean} hasComparison
 */
export function isComparisonSeries(series, index, hasComparison) {
  return /previous/i.test(series.key) || (hasComparison && index === 1);
}

/**
 * @param {{ data: SeriesWidgetData, showDataLabels?: boolean, isCumulative?: boolean, colorName?: string | null, hasComparison?: boolean }} props
 */
export function LineChart({ data, showDataLabels = false, isCumulative = false, colorName, hasComparison = false }) {
  const [containerRef, size] = useElementSize();
  const [hoverIndex, setHoverIndex] = useState(/** @type {number | null} */ (null));
  const [pointer, setPointer] = useState(/** @type {{ x: number, y: number, source?: 'pointer' | 'keyboard' | 'touch', href?: string | null }} */ ({ x: 0, y: 0 }));
  const drill = useDrilldown();
  const [gradientId] = useState(() => `line-area-${(gradientCounter += 1)}`);
  const { plotted, extras } = splitPlottedSeries(data);
  const format = data.format;
  const categories = data.categories;
  const series = plotted.map((item) => {
    if (!isCumulative) return item;
    let sum = 0;
    return { ...item, values: item.values.map((value) => (sum += Number(value ?? 0))) };
  });
  const styles = series.map((item, index) => {
    const isComparison = series.length > 1 && isComparisonSeries(item, index, hasComparison);
    const paletteColor = isComparison ? COMPARISON_SERIES_COLOR : series.length === 1 ? getSingleSeriesColor(colorName) : getSeriesColor(index);
    // cor com significado mandada pelo indicador (matrículas em verde, ano anterior em cinza), como nas barras
    const seriesColor = item.color;
    return { color: seriesColor ? `var(--report-series-${seriesColor}, ${paletteColor})` : paletteColor, dashed: isComparison };
  });
  const width = size.width;
  const height = size.height;

  // pontos clicáveis: cada ponto (categoria x série) com registros para este papel; a acumulada abre do início até ele
  /** @type {LinePoint[]} */
  const linePoints = [];
  if (drill) {
    categories.forEach((category, categoryIndex) => series.forEach((item, seriesIndex) => {
      const target = drill.target({ part: 'category', key: category.key, seriesKey: item.key });
      if (!target) return;
      linePoints.push({
        id: `${categoryIndex}:${seriesIndex}`, categoryIndex, seriesIndex, href: target.href,
        label: series.length > 1 ? `${category.label} · ${item.label}` : category.label,
        valueText: formatMetricValue(item.values[categoryIndex], format),
      });
    }));
  }
  const pointById = new Map(linePoints.map((point) => [point.id, point]));
  /** @param {number} index @param {Element} element @param {'keyboard' | 'touch'} source @param {string | null} [href] */
  const showAt = (index, element, source, href = null) => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const box = element.getBoundingClientRect();
    setHoverIndex(index);
    setPointer({ x: box.left + box.width / 2 - bounds.left, y: Math.max(96, box.top + box.height / 2 - bounds.top), source, href });
  };
  const navigation = useChartPointNavigation({
    points: linePoints,
    label: drill?.widgetTitle ?? '',
    onFocusPoint: (point, element) => showAt(point.categoryIndex, element, 'keyboard'),
    onBlurPoint: () => {
      if (pointer.source === 'keyboard') setHoverIndex(null);
    },
    onPin: (point, event) => showAt(point.categoryIndex, /** @type {Element} */ (event.currentTarget), 'touch', point.href),
    onUnpin: () => setHoverIndex(null),
  });

  let content = null;
  if (width > 0 && height > 0 && categories.length > 0) {
    const values = series.flatMap((item) => item.values.map((value) => Number(value ?? 0)));
    const scale = buildNiceScale(Math.min(0, ...values), Math.max(0, ...values), Math.max(2, Math.min(6, Math.floor(height / 45))), { integer: format === 'NUMBER' && areAllIntegers(values) });
    const tickLabels = scale.ticks.map((tick) => formatMetricValue(tick, format, { compact: true }));
    const left = Math.max(28, ...tickLabels.map((label) => estimateTextWidth(label) + 10));
    const right = 12;
    const top = showDataLabels ? 18 : 10;
    const bottom = 22;
    const plotWidth = Math.max(10, width - left - right);
    const plotHeight = Math.max(10, height - top - bottom);
    const step = categories.length > 1 ? plotWidth / (categories.length - 1) : 0;
    const toX = (/** @type {number} */ index) => (categories.length > 1 ? left + index * step : left + plotWidth / 2);
    const toY = (/** @type {number} */ value) => top + plotHeight * (1 - (value - scale.min) / (scale.max - scale.min || 1));
    const maxLabelWidth = Math.max(...categories.map((category) => estimateTextWidth(category.label))) + 10;
    const labelStep = Math.max(1, Math.ceil(maxLabelWidth / Math.max(1, step || plotWidth)));
    const showPoints = categories.length <= Math.floor(plotWidth / 10);
    const axisLabels = layoutCategoryLabels(categories.flatMap((category, index) => (index % labelStep === 0
      ? [{ key: category.key, text: truncateToWidth(category.label, maxLabelWidth * labelStep), x: toX(index), isFirst: index === 0, isLast: index === categories.length - 1 }]
      : [])), { width });
    const linePaths = series.map((item) =>
      item.values.map((value, index) => `${index === 0 ? 'M' : 'L'}${toX(index).toFixed(1)},${toY(Number(value ?? 0)).toFixed(1)}`).join(''),
    );
    const mainIndex = styles.findIndex((style) => !style.dashed);
    const areaPath = mainIndex >= 0 && categories.length > 1
      ? `${linePaths[mainIndex]}L${toX(categories.length - 1).toFixed(1)},${toY(Math.max(0, scale.min)).toFixed(1)}L${toX(0).toFixed(1)},${toY(Math.max(0, scale.min)).toFixed(1)}Z`
      : null;

    content = html`<svg width=${width} height=${height} class="chart-svg" ...${navigation.containerProps}
      onPointerMove=${(/** @type {PointerEvent} */ event) => {
        // a dica fixada pelo toque só sai com um toque fora do gráfico
        if (event.pointerType === 'touch' || pointer.source === 'touch') return;
        const bounds = containerRef.current?.getBoundingClientRect();
        if (!bounds) return;
        const x = event.clientX - bounds.left;
        const index = categories.length > 1 ? Math.round((x - left) / step) : 0;
        setHoverIndex(Math.max(0, Math.min(categories.length - 1, index)));
        setPointer({ x, y: event.clientY - bounds.top, source: 'pointer' });
      }}
      onPointerLeave=${() => {
        if (pointer.source !== 'touch') setHoverIndex(null);
      }}
    >
      <defs>
        <linearGradient id=${gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stop-color=${mainIndex >= 0 ? styles[mainIndex].color : 'transparent'} stop-opacity="0.18" />
          <stop offset="100%" stop-color=${mainIndex >= 0 ? styles[mainIndex].color : 'transparent'} stop-opacity="0.01" />
        </linearGradient>
      </defs>
      ${scale.ticks.map((tick, index) => html`<g key=${`tick-${tick}`}>
        <line class=${tick === 0 ? 'chart-axis-line' : 'chart-grid-line'} x1=${left} x2=${left + plotWidth} y1=${toY(tick)} y2=${toY(tick)} />
        <text class="chart-axis-text" x=${left - 8} y=${toY(tick)} text-anchor="end" dominant-baseline="middle">${tickLabels[index]}</text>
      </g>`)}
      ${axisLabels.map((label) => html`<text key=${label.key} class="chart-axis-text" x=${label.x} y=${top + plotHeight + 15} text-anchor=${label.anchor}>${label.text}</text>`)}
      ${areaPath ? html`<path d=${areaPath} fill=${`url(#${gradientId})`} />` : null}
      ${hoverIndex !== null ? html`<line class="chart-crosshair" x1=${toX(hoverIndex)} x2=${toX(hoverIndex)} y1=${top} y2=${top + plotHeight} />` : null}
      ${series.map((item, seriesIndex) => html`<g key=${item.key}>
        <path class="chart-line" d=${linePaths[seriesIndex]} stroke=${styles[seriesIndex].color} stroke-dasharray=${styles[seriesIndex].dashed ? '5 4' : undefined} />
        ${item.values.map((value, index) => (showPoints || hoverIndex === index
          ? html`<circle key=${index} class="chart-point" cx=${toX(index)} cy=${toY(Number(value ?? 0))} r=${hoverIndex === index ? 4.5 : 3}
              data-hollow=${styles[seriesIndex].dashed ? 'true' : undefined}
              fill=${styles[seriesIndex].dashed ? 'var(--t-background-secondary)' : styles[seriesIndex].color} stroke=${styles[seriesIndex].color} />`
          : null))}
        ${item.values.map((value, index) => {
          const point = pointById.get(`${index}:${seriesIndex}`);
          return point
            ? html`<a key=${`hit-${index}`} ...${navigation.pointProps(point, point)}>
                <circle class="chart-point-hit" cx=${toX(index)} cy=${toY(Number(value ?? 0))} r=${POINT_HIT_RADIUS} />
              </a>`
            : null;
        })}
        ${showDataLabels && !styles[seriesIndex].dashed
          ? item.values.map((value, index) => (index % labelStep === 0 && value
            ? html`<text key=${`label-${index}`} class="chart-data-label" x=${toX(index)} y=${toY(Number(value)) - 8} text-anchor="middle">${formatMetricValue(value, format, { compact: true })}</text>`
            : null))
          : null}
      </g>`)}
    </svg>`;
  }

  return html`<div class="chart">
    <div class="chart-plot" ref=${containerRef}>
      ${content}
      ${hoverIndex !== null && categories[hoverIndex]
        ? html`<${ChartTooltip}
            x=${pointer.x}
            y=${pointer.y - 10}
            containerWidth=${width}
            title=${categories[hoverIndex].label}
            rows=${[
              ...series.map((item, index) => ({ key: item.key, label: item.label, value: formatMetricValue(item.values[hoverIndex], format), color: styles[index].color, dashed: styles[index].dashed })),
              ...extras.map((item) => ({ key: item.key, label: item.label, value: formatMetricValue(item.values[hoverIndex], item.format ?? format) })),
            ]}
            hint=${linePoints.some((point) => point.categoryIndex === hoverIndex) ? (pointer.source === 'keyboard' ? 'keyboard' : 'pointer') : null}
            action=${pointer.source === 'touch' && pointer.href
              ? { href: pointer.href, onClick: (/** @type {MouseEvent} */ event) => handleDrillLinkClick(event, /** @type {string} */ (pointer.href)) }
              : null}
          />`
        : null}
    </div>
    <${ChartLegend} items=${series.map((item, index) => ({ key: item.key, label: item.label, color: styles[index].color, dashed: styles[index].dashed }))} />
  </div>`;
}
